import { isNil } from "client/Misc/utils";
import { NodeRange, NodeType, Schema } from "prosemirror-model";
import { liftListItem, wrapInList } from "prosemirror-schema-list";
import { Command, EditorState } from "prosemirror-state";
import { useMemo } from "react";
import { withRestoreDefaultFontMarksOnNewParagraph } from "../commands/withRestoreDefaultFontMarksOnNewParagraph";
import { hasNodes } from "../schema";

export type ListToolHandlers = {
	areSupportedBySchema: true;
	isInBulletList: boolean;
	isInOrderedList: boolean;
	toggleBulletListHandler(): void;
	toggleOrderedListHandler(): void;
} | {
	areSupportedBySchema: false;
};

export function useListTools(
	editorState: EditorState,
	applyCommandSequence: (commands: readonly Command[]) => void,
	focus?: () => void
): ListToolHandlers {
	return useMemo(
		(): ListToolHandlers => {
			const schema: Schema<never, never> = editorState.schema;

			if (!hasNodes(
				schema,
				[
					"listItem",
					"orderedList",
					"bulletList"
				]
			)) {
				return {
					areSupportedBySchema: false
				};
			}

			const isInList = (listType: NodeType): boolean => {
				const {
					$from,
					$to
				} = editorState.selection;

				const range: NodeRange | nil = $from.blockRange(
					$to,
					node =>
						node.childCount > 0
						&& node.firstChild!.type === schema.nodes.listItem
				);

				if (isNil(range)) {
					return false;
				}

				const {
					parent
				} = range;

				return parent.type === listType;
			};

			const toggleList = (listType: NodeType): void => {
				const itemType: NodeType = schema.nodes.listItem;

				const {
					$from,
					$to
				} = editorState.selection;

				const range: NodeRange | nil = $from.blockRange(
					$to,
					node =>
						node.childCount > 0
						&& node.firstChild!.type === itemType
				);

				if (isNil(range)) {
					applyCommandSequence([
						withRestoreDefaultFontMarksOnNewParagraph(wrapInList(listType))
					]);
					return;
				}

				const {
					parent
				} = range;

				if (parent.type !== listType) {
					applyCommandSequence([
						withRestoreDefaultFontMarksOnNewParagraph(liftListItem(itemType)),
						withRestoreDefaultFontMarksOnNewParagraph(wrapInList(listType))
					]);

					return;
				}

				applyCommandSequence([
					withRestoreDefaultFontMarksOnNewParagraph(liftListItem(itemType))
				]);
			};

			return {
				areSupportedBySchema: true,
				toggleOrderedListHandler(): void {
					toggleList(schema.nodes.orderedList);
					focus?.();
				},
				toggleBulletListHandler(): void {
					toggleList(schema.nodes.bulletList);
					focus?.();
				},
				isInOrderedList: isInList(schema.nodes.orderedList),
				isInBulletList: isInList(schema.nodes.bulletList)
			};
		},
		[
			applyCommandSequence,
			editorState,
			focus
		]
	);
};